"use client";

import { cn } from "@homeinstead/ui";
import { useLeadFlowRuntime } from "../LeadFlowRuntimeContext";
import { useLeadFlowStep } from "../LeadFlowStepContext";

const HEADING_CLASS = "font-jakarta text-2xl leading-snug md:text-3xl";

/** The hero takeover's step heading, larger from `md`. */
const TAKEOVER_HEADING_CLASS = "font-bold font-jakarta text-xl md:text-1xl";

/** Takeover helper text, set well apart from the heading above it. */
const TAKEOVER_HELPER_TEXT_CLASS = "mt-10 block font-bold text-base leading-6";

const SUBHEADING_CLASS = "font-bold text-lg";

type QuestionLegendProps = {
  helperText?: string | null;
  label?: string | null;
};

/**
 * Rules give up width before the label wraps: the label grows from its own
 * width and never shrinks below it, short of the fieldset's width. It takes
 * the remaining space so the divider spans the full fieldset. Rules use the
 * text colour so they read on both light and dark sections.
 */
function LeadFlowRuledSubheading({ label }: { label: string }) {
  return (
    <span className="flex min-h-8 w-full items-center font-bold font-jakarta text-lg leading-7">
      <span
        aria-hidden="true"
        className="h-px w-16 min-w-0 shrink bg-current"
      />
      <span className="max-w-full flex-[1_0_auto] px-4 text-center">
        {label}
      </span>
      <span
        aria-hidden="true"
        className="h-px w-16 min-w-0 shrink bg-current"
      />
    </span>
  );
}

/**
 * The question text as its fieldset's legend. The in-page first step uses
 * a subheading, ruled on a dark theme. Every other step, including a first
 * step that opens on a surface, uses the heading so it can take focus.
 */
function QuestionLegend({ helperText, label }: QuestionLegendProps) {
  const { isSectionTakeover = false, themeContrast } = useLeadFlowRuntime();
  const { isFirstStep } = useLeadFlowStep();

  if (!label) {
    return null;
  }

  return (
    <legend
      className={cn(
        "w-full max-w-full p-0",
        isSectionTakeover && !helperText ? "mb-8" : "mb-4",
      )}
    >
      {isFirstStep ? (
        themeContrast === "light" ? (
          <LeadFlowRuledSubheading label={label} />
        ) : (
          <span className={cn("block", SUBHEADING_CLASS)}>{label}</span>
        )
      ) : (
        <h2
          className={isSectionTakeover ? TAKEOVER_HEADING_CLASS : HEADING_CLASS}
          data-lead-flow-step-heading=""
          tabIndex={-1}
        >
          {label}
        </h2>
      )}
      {helperText ? (
        <span
          className={
            isSectionTakeover
              ? TAKEOVER_HELPER_TEXT_CLASS
              : "mt-1 block text-sm"
          }
        >
          {helperText}
        </span>
      ) : null}
    </legend>
  );
}

export { QuestionLegend };
